import dayjs, { type Dayjs } from "dayjs";
import isLeapYear from "dayjs/plugin/isLeapYear";

import {
  INCOME_RATIO,
  NET_PAY_FACTOR,
  PAY_FREQUENCY,
  PAY_FREQUENCY_FACTOR,
  type PayFrequency,
  TAX_RATES,
} from "./incomeCalculator.constants";
import type { CalculatorInput, IncomeBreakdownResult, TaxesBreakdown } from "./incomeCalculator.types";

// eslint-disable-next-line import/no-named-as-default-member
dayjs.extend(isLeapYear);

/**
 * Two-decimal rounding matching dealer-ui / CAPS2 conventions.
 * @param {number} value - The number to round.
 * @returns {number} The value rounded to two decimal places.
 */
function round(value: number): number {
  return Number((Math.round(value * 100) / 100).toFixed(2));
}

/**
 * Helper to adjust days based on pay date for 2x-Monthly period stepping.
 * Ported verbatim from dealer-ui util/IncomeCalculator.ts adjustDays.
 * @param {Date} payDate - The pay date to inspect.
 * @returns {number} The number of days to subtract for the prior 2x-Monthly period.
 */
export function adjustDays(payDate: Date): number {
  let adjustedDays = 15;
  const payMonth = payDate.getMonth() + 1;
  const payDay = payDate.getDate();

  if (
    (payMonth === 2 || payMonth === 4 || payMonth === 6 || payMonth === 8 || payMonth === 9 || payMonth === 11) &&
    payDay < 15
  ) {
    adjustedDays = 16;
  }

  if (payMonth === 3 && payDay < 14) {
    adjustedDays = dayjs(payDate).isLeapYear() ? 14 : 13;
  }

  if (payDay === 31) {
    adjustedDays = 16;
  }

  if (payMonth === 2 && payDay === 28) {
    adjustedDays = 13;
  } else if (payMonth === 2 && payDay === 29) {
    adjustedDays = 14;
  }

  return adjustedDays;
}

/**
 * Calculates the number of pay periods from hire date to pay date for the
 * current year, accounting for partial first periods and leap-year adjustments.
 * Ported verbatim from dealer-ui util/IncomeCalculator.ts calculatePayPeriods.
 * @param {PayFrequency} payFrequency - The pay frequency enum value.
 * @param {Dayjs} payDate - The most recent pay date.
 * @param {Dayjs} hireDate - The applicant's hire date.
 * @param {Dayjs | undefined} payPeriodEndDate - Optional pay-period end date.
 * @param {boolean} isPaidOn1stAnd15 - Whether the applicant is paid on 1st and 15th.
 * @returns {number} The computed pay-period count for the year.
 */
export function calculatePayPeriods(
  payFrequency: PayFrequency,
  payDate: Dayjs,
  hireDate: Dayjs,
  payPeriodEndDate: Dayjs | undefined,
  isPaidOn1stAnd15: boolean
): number {
  let temporaryPayDate = payDate;
  let temporaryDenominator = 0;
  let temporaryNumerator = 0;
  let temporaryPayPeriods = 0;
  let temporaryPayDateOriginalDate;
  let daysToOffsetMinusFromYear = 0;
  temporaryPayDateOriginalDate = payDate;
  const hireDateYear = hireDate.get("year");
  const payDateYear = payDate.get("year");

  const temporaryHireDate = hireDateYear < payDateYear ? payDate.startOf("year") : hireDate;

  if (hireDateYear === payDateYear) {
    const selectedPayDate = payPeriodEndDate ?? payDate;
    temporaryPayDate = selectedPayDate;
    temporaryPayDateOriginalDate = selectedPayDate;
  }

  while (temporaryPayDate >= temporaryHireDate) {
    temporaryPayPeriods = temporaryPayPeriods + 1;
    switch (payFrequency) {
      case PAY_FREQUENCY.WEEKLY: {
        temporaryPayDate = temporaryPayDate.subtract(7, "day");
        break;
      }
      case PAY_FREQUENCY.BI_WEEKLY: {
        temporaryPayDate = temporaryPayDate.subtract(14, "day");
        break;
      }
      case PAY_FREQUENCY.TWICE_MONTHLY: {
        temporaryPayDate = temporaryPayDate.subtract(adjustDays(temporaryPayDate.toDate()), "day");
        break;
      }
      case PAY_FREQUENCY.MONTHLY: {
        temporaryPayDate = temporaryPayDate.subtract(1, "month");
        break;
      }
      case PAY_FREQUENCY.ANNUAL: {
        const yearPlaceholder = temporaryPayDate.get("year");
        const originalTemporaryDate = temporaryPayDate.set("year", yearPlaceholder);
        temporaryPayDate = temporaryPayDate.subtract(1, "year");
        const adjustedTemporaryDate = temporaryPayDate.set("year", yearPlaceholder);
        daysToOffsetMinusFromYear = Math.abs(originalTemporaryDate.diff(adjustedTemporaryDate, "day"));
        break;
      }
      // No default
    }
  }

  switch (payFrequency) {
    case PAY_FREQUENCY.WEEKLY: {
      temporaryDenominator = 7;
      temporaryPayDate = temporaryPayDate.add(7, "day");
      break;
    }
    case PAY_FREQUENCY.BI_WEEKLY: {
      temporaryDenominator = 14;
      temporaryPayDate = temporaryPayDate.add(14, "day");
      break;
    }
    case PAY_FREQUENCY.TWICE_MONTHLY: {
      temporaryDenominator = 15;
      temporaryPayDate = temporaryPayDate.add(15, "day");
      break;
    }
    case PAY_FREQUENCY.MONTHLY: {
      temporaryDenominator = temporaryPayDateOriginalDate.daysInMonth();
      temporaryPayDate = temporaryPayDate.add(1, "month");
      break;
    }
    case PAY_FREQUENCY.ANNUAL: {
      temporaryPayDate = temporaryPayDate.add(1, "year").add(daysToOffsetMinusFromYear, "day");
      temporaryDenominator = dayjs(temporaryPayDate).isLeapYear() ? 366 : 365;
      break;
    }
    // No default
  }

  if (hireDate) {
    const startDate = dayjs(hireDate);
    const endDate = dayjs(temporaryPayDate);
    const days = hireDate === undefined ? 0 : endDate.diff(startDate, "day");
    temporaryNumerator = days + 1;
  }

  if (
    hireDateYear < payDateYear &&
    payFrequency === PAY_FREQUENCY.TWICE_MONTHLY &&
    payDate.get("date") === 15 &&
    isPaidOn1stAnd15
  ) {
    temporaryPayPeriods = temporaryPayPeriods + 1;
  }

  if (temporaryDenominator !== 0 && temporaryNumerator / temporaryDenominator < 1) {
    temporaryPayPeriods = temporaryPayPeriods - 1 + temporaryNumerator / temporaryDenominator;
  }

  return temporaryPayPeriods;
}

/**
 * Computes the full income + tax breakdown from the provided calculator inputs.
 * Implements CAPS2 Step2Applicant.calculateIncome / calculateTaxes formulas,
 * extended with weekly / bi-weekly / stip% / FICA breakdown.
 * Returns zeroed results when payPeriods <= 0 (guard matching dealer-ui).
 * @param {CalculatorInput} input - The calculator input values.
 * @returns {IncomeBreakdownResult} The computed income and tax breakdown.
 */
export function calculateIncomeBreakdown(input: CalculatorInput): IncomeBreakdownResult {
  const {
    payFrequency,
    grossPayPerPeriod,
    grossPayYtd,
    payDate,
    payPeriodEndDate,
    isPaidOn1stAnd15 = false,
    hireDate,
    payType = "Gross",
  } = input;

  // When payType is "Net", gross up both the per-period and YTD bases by
  // NET_PAY_FACTOR (1.21) before ALL math (income + taxes), per parity spec.
  const netMultiplier = payType === "Net" ? NET_PAY_FACTOR : 1;
  const effectivePeriod = grossPayPerPeriod * netMultiplier;
  const effectiveYtd = grossPayYtd * netMultiplier;

  const factor = PAY_FREQUENCY_FACTOR[payFrequency];

  const payPeriods = calculatePayPeriods(
    payFrequency,
    dayjs(payDate),
    dayjs(hireDate),
    payPeriodEndDate ? dayjs(payPeriodEndDate) : undefined,
    isPaidOn1stAnd15
  );

  const zeroedTaxes: TaxesBreakdown = {
    socialSecurityPeriod: 0,
    socialSecurityYtd: 0,
    medicarePeriod: 0,
    medicareYtd: 0,
    ficaCombinedPeriod: 0,
    ficaCombinedYtd: 0,
  };

  if (payPeriods <= 0) {
    return {
      monthlyIncomePeriod: 0,
      monthlyIncomeYtd: 0,
      weeklyIncome: 0,
      biWeeklyIncome: 0,
      payPeriods: 0,
      stipPct: 0,
      ratioThreshold: "ok",
      taxes: zeroedTaxes,
    };
  }

  // Income calculations (CAPS2 Step2Applicant.calculateIncome / calculateTaxes)
  const monthlyIncomePeriod = round(effectivePeriod * factor);
  const monthlyIncomeYtd = round(factor * (effectiveYtd / payPeriods));
  const weeklyIncome = round(monthlyIncomeYtd / factor);
  const biWeeklyIncome = round((monthlyIncomeYtd * 12) / 26);

  // Stip% — guard division by zero when monthlyIncomeYtd is 0
  const ratio = monthlyIncomeYtd === 0 ? 0 : monthlyIncomePeriod / monthlyIncomeYtd;
  const stipPct = round(ratio * 100);

  // Ratio threshold warning (parity spec / dealer-ui ratiolessthan1 / ratiomorethan1).
  // Guards zero-YTD: ratio is 0 when monthlyIncomeYtd === 0, which falls below LOW,
  // but we treat it as "ok" to avoid a spurious warning on zero data.
  let ratioThreshold: "low" | "high" | "ok";
  if (monthlyIncomeYtd === 0) {
    ratioThreshold = "ok";
  } else if (ratio < INCOME_RATIO.LOW) {
    ratioThreshold = "low";
  } else if (ratio > INCOME_RATIO.HIGH) {
    ratioThreshold = "high";
  } else {
    ratioThreshold = "ok";
  }

  // Taxes are computed from the effective (net-adjusted) per-period and YTD bases,
  // matching CAPS2 Step6ApplicantIncomeModelBean.calculateTaxes(). See computeTaxes().
  const taxes = computeTaxes(effectivePeriod, effectiveYtd, payDate);

  return {
    monthlyIncomePeriod,
    monthlyIncomeYtd,
    weeklyIncome,
    biWeeklyIncome,
    payPeriods,
    stipPct,
    ratioThreshold,
    taxes,
  };
}

/**
 * Computes the Social Security / Medicare / FICA breakdown from raw per-period
 * and YTD gross pay. Parity with CAPS2 Step6ApplicantIncomeModelBean.calculateTaxes():
 * period columns tax the raw grossPayPerPeriod; YTD columns tax the raw
 * grossPayYtd directly (no annualization). The SS rate switches on the
 * pay-date year (>= 2013 → 6.2%, < 2013 → 4.2%); Medicare is a flat 1.45%;
 * FICA = SS + Medicare; no Social Security wage-base cap is applied.
 * When payDate is omitted, the current year's rate applies — so the Show Taxes
 * panel can compute without a payDate.
 * When payType is "Net", both bases are grossed up by NET_PAY_FACTOR (1.21)
 * before tax computation, matching the income calc treatment.
 * @param {number} grossPayPerPeriod - The raw gross pay for one pay period.
 * @param {number} grossPayYtd - The raw gross pay accumulated year-to-date.
 * @param {string} [payDate] - Pay date (ISO "YYYY-MM-DD"); selects the SS rate year.
 * @param {"Gross" | "Net"} [payType] - Gross/Net pay type; defaults to "Gross".
 * @returns {TaxesBreakdown} The per-period and YTD tax figures.
 */
export function computeTaxes(
  grossPayPerPeriod: number,
  grossPayYtd: number,
  payDate?: string,
  payType?: "Gross" | "Net"
): TaxesBreakdown {
  const netMultiplier = payType === "Net" ? NET_PAY_FACTOR : 1;
  const perPeriod = (Number(grossPayPerPeriod) || 0) * netMultiplier;
  const ytd = (Number(grossPayYtd) || 0) * netMultiplier;

  const year = payDate ? dayjs(payDate).year() : dayjs().year();
  const ssRate = year >= 2013 ? TAX_RATES.SS_2013_PLUS : TAX_RATES.SS_PRE_2013;
  const medRate = TAX_RATES.MEDICARE;

  const socialSecurityPeriod = round(perPeriod * ssRate);
  const socialSecurityYtd = round(ytd * ssRate);
  const medicarePeriod = round(perPeriod * medRate);
  const medicareYtd = round(ytd * medRate);

  return {
    socialSecurityPeriod,
    socialSecurityYtd,
    medicarePeriod,
    medicareYtd,
    ficaCombinedPeriod: round(socialSecurityPeriod + medicarePeriod),
    ficaCombinedYtd: round(socialSecurityYtd + medicareYtd),
  };
}
